import React, { useState } from 'react'
import { HeartIcon as HeartOutline } from '@heroicons/react/24/outline'
import { HeartIcon as HeartSolid } from '@heroicons/react/24/solid'
import clsx from 'clsx'
import { toggleNewsLike } from '../../services/informativo'
import type { NewsLikeSummary } from '../../types/informativo'

type Props = {
  newsId: string
  summary: NewsLikeSummary
  onChanged: (newsId: string, next: NewsLikeSummary) => void
  className?: string
  /** Contraste para banner colorido (hero). */
  onDark?: boolean
}

const NewsLikeButton: React.FC<Props> = ({
  newsId,
  summary,
  onChanged,
  className,
  onDark = false,
}) => {
  const [busy, setBusy] = useState(false)

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (busy || !newsId) return
    setBusy(true)
    try {
      const next = await toggleNewsLike(newsId)
      onChanged(newsId, next)
    } catch {
      // Mantém o estado atual; o pai pode exibir erro se quiser
    } finally {
      setBusy(false)
    }
  }

  const liked = summary.likedByMe
  const Icon = liked ? HeartSolid : HeartOutline

  return (
    <button
      type="button"
      onClick={(e) => void handleClick(e)}
      disabled={busy || !newsId}
      title={liked ? 'Remover curtida' : 'Curtir'}
      aria-pressed={liked}
      aria-label={liked ? 'Remover curtida' : 'Curtir notícia'}
      className={clsx(
        'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition disabled:opacity-50',
        onDark
          ? liked
            ? 'bg-white/25 text-white hover:bg-white/35'
            : 'bg-white/15 text-white/90 hover:bg-white/25'
          : liked
            ? 'bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/40'
            : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600',
        className
      )}
    >
      <Icon className={clsx('h-4 w-4', liked && !onDark && 'text-red-500')} />
      <span className="tabular-nums">{summary.count}</span>
    </button>
  )
}

export default NewsLikeButton
